import { useState } from 'react';
import { RefreshIconButton } from '../../../shared/ui/RefreshIconButton.jsx';
import { useSiteSupport } from '../hooks/useUnionSupport.js';

const CATEGORY_LABELS = {
  general: '일반 문의',
  account: '계정·인증',
  bug: '오류 신고',
  suggestion: '기능 제안',
  data: '데이터 정정·삭제',
};

const STATUS_LABELS = {
  open: '미확인',
  in_progress: '처리 중',
  resolved: '완료',
};

const EMPTY_FORM = { category: 'general', title: '', body: '' };

function dateLabel(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function StatusBadge({ status }) {
  const normalized = STATUS_LABELS[status] ? status : 'open';
  return <span className={`union-support-status ${normalized}`}>{STATUS_LABELS[normalized]}</span>;
}

function MemberSupport({ support, mobile }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const submit = async (event) => {
    event.preventDefault();
    if (await support.create(form)) setForm(EMPTY_FORM);
  };

  return (
    <>
      <form className="union-support-form" onSubmit={submit}>
        <header><div><span>NEW SITE SUPPORT</span><h3>사이트 문의 등록</h3></div><small>DoroParty 운영자에게만 공개됩니다.</small></header>
        <label><span>분류</span><select value={form.category} onChange={(event) => update('category', event.target.value)}>{Object.entries(CATEGORY_LABELS).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
        <label><span>제목</span><input maxLength={120} value={form.title} onChange={(event) => update('title', event.target.value)} placeholder="문의 제목" /></label>
        <label><span>내용</span><textarea maxLength={5000} rows={mobile ? 7 : 9} value={form.body} onChange={(event) => update('body', event.target.value)} placeholder="사이트 이용 중 확인이 필요한 내용과 발생 상황을 적어주세요." /></label>
        <footer><small>{form.body.length}/5000</small><button type="submit" disabled={Boolean(support.working) || !form.title.trim() || !form.body.trim()}>{support.working === 'create' ? '등록 중…' : '문의 등록'}</button></footer>
      </form>

      <section className="union-support-list" aria-label="내 사이트 문의 목록">
        <header><div><span>MY SITE REQUESTS</span><h3>내 문의</h3></div><strong>{support.requests.length}</strong></header>
        {support.requests.map((item) => (
          <article className={`union-support-item ${item.status}`} key={item.id}>
            <header><div><span>{CATEGORY_LABELS[item.category] || CATEGORY_LABELS.general}</span><h4>{item.title}</h4></div><StatusBadge status={item.status} /></header>
            <p>{item.body}</p>
            <time dateTime={item.createdAt || ''}>{dateLabel(item.createdAt)}</time>
            {item.adminReply ? <div className="union-support-reply"><strong>DoroParty 답변</strong><p>{item.adminReply}</p><time dateTime={item.updatedAt || ''}>{dateLabel(item.updatedAt)}</time></div> : <div className="union-support-waiting">사이트 운영자 답변을 기다리고 있습니다.</div>}
          </article>
        ))}
        {!support.requests.length ? <div className="union-support-empty"><strong>등록한 사이트 문의가 없습니다.</strong><p>계정·오류·기능·데이터 관련 내용을 문의할 수 있습니다.</p></div> : null}
      </section>
    </>
  );
}

function ManagedSupportItem({ item, support }) {
  const [status, setStatus] = useState(item.status);
  const [adminReply, setAdminReply] = useState(item.adminReply);
  const save = () => support.update({ id: item.id, status, adminReply });
  const authorLabel = [item.authorName, item.authorEmail].filter(Boolean).join(' · ') || '사용자';
  return (
    <article className={`union-support-item managed ${status}`}>
      <header>
        <div><span>{CATEGORY_LABELS[item.category] || CATEGORY_LABELS.general} · {authorLabel}</span><h4>{item.title}</h4><time dateTime={item.createdAt || ''}>{dateLabel(item.createdAt)}</time></div>
        <StatusBadge status={status} />
      </header>
      <p>{item.body}</p>
      <div className="union-support-manager-editor">
        <label><span>처리 상태</span><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="open">미확인</option><option value="in_progress">처리 중</option><option value="resolved">완료</option></select></label>
        <label><span>답변</span><textarea maxLength={5000} rows={5} value={adminReply} onChange={(event) => setAdminReply(event.target.value)} placeholder="사용자에게 전달할 사이트 운영 답변을 입력하세요." /></label>
        <footer><small>{adminReply.length}/5000</small><button type="button" disabled={Boolean(support.working)} onClick={save}>{support.working === `update:${item.id}` ? '저장 중…' : '저장'}</button></footer>
      </div>
    </article>
  );
}

function ManagerSupport({ support }) {
  const pendingCount = support.requests.filter((item) => item.status !== 'resolved').length;
  return (
    <section className="union-support-list managed-list" aria-label="사이트 문의 관리 목록">
      <header><div><span>SITE SUPPORT INBOX</span><h3>사이트 문의 관리</h3><p>DoroParty 이용자가 등록한 문의를 확인하고 답변합니다.</p></div><strong>{pendingCount}</strong></header>
      {support.requests.map((item) => <ManagedSupportItem key={`${item.id}:${item.updatedAt || ''}`} item={item} support={support} />)}
      {!support.requests.length ? <div className="union-support-empty"><strong>접수된 사이트 문의가 없습니다.</strong><p>새 문의가 등록되면 관리 아이콘에 숫자로 표시됩니다.</p></div> : null}
    </section>
  );
}

export function SiteSupportBoard({ mobile = false, manage = false }) {
  const support = useSiteSupport({ manage });
  if (support.loading) return <section className={`union-support-state ${mobile ? 'mobile' : ''}`}><span /><p>사이트 문의 목록을 불러오는 중입니다.</p></section>;

  return (
    <section className={`union-support-board ${mobile ? 'mobile' : 'desktop'} ${manage ? 'manager' : 'member'}`} aria-label={manage ? '사이트 문의 관리' : '사이트 문의'}>
      <header className="union-support-heading">
        <div><p>DOROPARTY SUPPORT</p><h2>{manage ? '사이트 문의 관리' : 'DoroParty 문의'}</h2><span>{manage ? '플랫폼 관리자만 전체 사이트 문의를 확인하고 처리할 수 있습니다.' : '문의 내용은 본인과 DoroParty 플랫폼 관리자만 확인할 수 있습니다.'}</span></div>
        <RefreshIconButton onClick={() => support.refresh()} />
      </header>
      {support.notice.message ? <p className={`union-support-feedback ${support.notice.type}`} role="status">{support.notice.message}</p> : null}
      {manage ? <ManagerSupport support={support} /> : <MemberSupport support={support} mobile={mobile} />}
    </section>
  );
}
